import { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
    CheckCircle2,
    Circle,
    ChevronDown,
    Sparkles,
    Image as ImageIcon,
    Newspaper,
    HeartHandshake,
    Globe2,
} from "lucide-react";

/**
 * Iter-27.74 / Iter-28 / Iter-29 — Mini score de complétude de l'espace partenaire.
 *
 * Calcule en local 4 items qui définissent un espace "premium et vivant" :
 *  1. Hero personnalisé (image/vidéo OU tagline)
 *  2. 3 actualités publiées (counts.news)
 *  3. 1 vidéo de remerciement (counts.gratitude)
 *  4. 1 histoire d'impact (counts.impact)
 *
 * Iter-29 — La "Mission rédigée" a été retirée : c'est l'équipe Shariti
 * Academy qui s'en occupe dans le back-office au moment de l'onboarding du
 * partenaire (avant de lui livrer ses identifiants). Pas besoin de la mettre
 * dans la complétude visible par l'asso.
 * Iter-28 — La FAQ a été retirée volontairement.
 *
 * Chaque item peut être cliqué pour naviguer vers l'onglet concerné (onGoToTab).
 * Le widget reste replié par défaut pour ne pas alourdir la sidebar.
 */
export default function AssoCompletionWidget({ asso, stats, onGoToTab, accent = "#EE722E" }) {
    const [open, setOpen] = useState(false);

    const items = useMemo(() => {
        const counts = stats?.counts || {};
        const tagline = (asso?.tagline || "").trim();
        const heroAsset = asso?.hero_image_url || asso?.hero_video_url;

        return [
            {
                id: "hero",
                tab: "space",
                Icon: ImageIcon,
                label: "Hero personnalisé",
                hint: "Image ou tagline d'accroche",
                done: Boolean(heroAsset || tagline.length >= 8),
            },
            {
                id: "news",
                tab: "news",
                Icon: Newspaper,
                label: "3 actualités publiées",
                hint: `${counts.news || 0}/3 publiées`,
                done: (counts.news || 0) >= 3,
            },
            {
                id: "gratitude",
                tab: "gratitude",
                Icon: HeartHandshake,
                label: "Vidéo de remerciement",
                hint: "Une selfie suffit",
                done: (counts.gratitude || 0) >= 1,
            },
            {
                id: "impact",
                tab: "impact",
                Icon: Globe2,
                label: "Histoire d'impact",
                hint: "Raconte une transformation",
                done: (counts.impact || 0) >= 1,
            },
        ];
    }, [asso, stats]);

    const total = items.length;
    const done = items.filter((it) => it.done).length;
    const pct = Math.round((done / total) * 100);
    const isComplete = done === total;

    // Iter-27.75 — Celebrate the exact moment the asso reaches 100 %.
    // Iter-27.79 fix: We MUST wait until both `asso` AND `stats` are actually
    // loaded before seeding the ref. Otherwise the first render computes
    // done=0 (everything undefined), then when stats arrives done jumps to 6,
    // and the transition fires falsely on every page reload / re-login.
    const prevDoneRef = useRef(null); // null = not seeded yet
    const dataReady = Boolean(asso && stats);
    useEffect(() => {
        if (!dataReady) return;
        const prev = prevDoneRef.current;
        if (prev === null) {
            // First time we have valid data → seed silently, no celebration.
            prevDoneRef.current = done;
            return;
        }
        if (prev < total && done === total) {
            toast.success("Espace 100 % complet — bravo !", {
                description: "Tes ambassadeurs vont adorer ton espace.",
                duration: 5000,
            });
            const duration = 1800;
            const end = Date.now() + duration;
            const colors = [accent, "#FCDB53", "#FFFFFF"];
            (function frame() {
                confetti({
                    particleCount: 4,
                    angle: 60,
                    spread: 60,
                    origin: { x: 0, y: 0.7 },
                    colors,
                });
                confetti({
                    particleCount: 4,
                    angle: 120,
                    spread: 60,
                    origin: { x: 1, y: 0.7 },
                    colors,
                });
                if (Date.now() < end) requestAnimationFrame(frame);
            })();
        }
        prevDoneRef.current = done;
    }, [done, total, accent, dataReady]);

    return (
        <div
            className="rounded-2xl p-3 bg-white/3 border border-white/8 backdrop-blur-sm"
            data-testid="asso-completion-widget"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2.5 text-left"
                data-testid="asso-completion-toggle"
                aria-expanded={open}
            >
                {/* Mini ring */}
                <div
                    className="relative w-9 h-9 shrink-0 rounded-full flex items-center justify-center"
                    style={{
                        background: `conic-gradient(${accent} ${pct}%, rgba(255,255,255,0.08) ${pct}%)`,
                    }}
                >
                    <div className="absolute inset-[3px] rounded-full bg-shariti-deep-black flex items-center justify-center">
                        {isComplete ? (
                            <Sparkles className="w-3.5 h-3.5" style={{ color: accent }} />
                        ) : (
                            <span className="text-[10px] font-extrabold text-white leading-none">
                                {done}/{total}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-w-0">
                    <p className="text-[9px] font-thin tracking-[0.22em] uppercase text-white/50 leading-none mb-0.5">
                        Complétude
                    </p>
                    <p className="text-[11px] font-extrabold text-white truncate">
                        {isComplete ? "Espace 100 % prêt" : `${pct} % de ton espace`}
                    </p>
                </div>

                <ChevronDown
                    className={`w-4 h-4 text-white/45 shrink-0 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="mt-3 space-y-1 overflow-hidden"
                        data-testid="asso-completion-list"
                    >
                        {items.map((it) => {
                            const Icon = it.Icon;
                            return (
                                <li key={it.id}>
                                    <button
                                        type="button"
                                        onClick={() => onGoToTab?.(it.tab)}
                                        data-testid={`asso-completion-item-${it.id}`}
                                        className={`group w-full flex items-center gap-2 px-2 py-1.5 rounded-xl text-left transition-all ${
                                            it.done
                                                ? "hover:bg-white/5"
                                                : "hover:bg-shariti-orange/8"
                                        }`}
                                    >
                                        {it.done ? (
                                            <CheckCircle2
                                                className="w-4 h-4 shrink-0"
                                                style={{ color: accent }}
                                            />
                                        ) : (
                                            <Circle className="w-4 h-4 shrink-0 text-white/30" />
                                        )}
                                        <Icon
                                            className={`w-3.5 h-3.5 shrink-0 ${
                                                it.done ? "text-white/55" : "text-white/35"
                                            }`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p
                                                className={`text-[11px] font-bold leading-tight truncate ${
                                                    it.done
                                                        ? "text-white/85 line-through decoration-white/30"
                                                        : "text-white"
                                                }`}
                                            >
                                                {it.label}
                                            </p>
                                            {!it.done && (
                                                <p className="text-[9px] text-white/45 font-light leading-tight truncate">
                                                    {it.hint}
                                                </p>
                                            )}
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
}
